/**
 * MinistryShell — chat-first layout for the MoHAP Transformation Agent.
 *
 * One chat. One agent. One screen. Two routes only:
 *   - /mohap        → MinistryChatPage (full-screen chat with NChatProvider)
 *   - /mohap/admin  → MinistryAdmin (configuration + audit)
 *
 * The shell is responsible for:
 *   1. Edition guard — non-Ministry users get bounced to /
 *   2. Membership guard — RLS denies; if no workspace visible, show access-denied
 *   3. Role guard — every member has a role in workspace_roles
 *   4. WORKSPACE ISOLATION — forces the active workspace to mohap on entry,
 *      so all downstream chat / context queries scope to the Ministry KG
 *      regardless of what the user had selected before. Without this, a
 *      user with both a personal workspace and the mohap workspace would
 *      leak personal-KG entities into Ministry chat answers.
 *   5. MOHAP branding tokens applied via [data-mohap]
 */

import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useMinistryEdition } from "./hooks/useMinistryEdition";
import { useMinistryWorkspace } from "./hooks/useMinistryWorkspace";
import { useMinistryRole } from "./hooks/useMinistryRole";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useTheme } from "@/contexts/ThemeContext";
import "./styles/mohap-tokens.css";

function ShellLoader({ label }: { label: string }) {
  return (
    <div data-mohap className="flex min-h-screen items-center justify-center">
      <span className="text-sm font-medium tracking-wider animate-pulse" style={{ color: "var(--mohap-text-muted)" }}>
        {label}
      </span>
    </div>
  );
}

export function MinistryShell() {
  const location = useLocation();
  const edition = useMinistryEdition();
  const { workspace, loading: wsLoading } = useMinistryWorkspace();
  const { role, loading: roleLoading } = useMinistryRole();
  const { currentWorkspace, switchWorkspace, workspaces } = useWorkspace();
  const { resolvedTheme, setTheme } = useTheme();

  // ── Force light mode in the Ministry edition ────────────────────────
  // The MoHAP tokens (cream surface / primary green / accent gold) are
  // designed for a light background and look wrong on dark. Override
  // any user/system preference while inside /mohap. Their preference is
  // preserved in localStorage and restored when they leave (mohap-only
  // users never leave, so this is effectively a permanent light-mode).
  useEffect(() => {
    if (resolvedTheme !== "light") setTheme("light");
  }, [resolvedTheme, setTheme]);

  // ── Workspace isolation enforcement ─────────────────────────────────
  // The chat queries kg_entities/raw_events/etc. by currentWorkspace.id.
  // When the user lands at /mohap, force the WorkspaceContext to point
  // at the Ministry workspace so personal KG never leaks into Ministry
  // answers. Runs once per workspace load.
  useEffect(() => {
    if (!workspace) return;
    const target = workspaces.find((w) => w.id === workspace.id);
    if (!target) return;                              // not yet loaded
    if (currentWorkspace?.id !== workspace.id) {
      switchWorkspace(workspace.id);
    }
  }, [workspace, workspaces, currentWorkspace?.id, switchWorkspace]);

  if (!edition.isWhiteLabel) {
    return <Navigate to="/" replace />;
  }

  if (wsLoading || roleLoading) {
    return <ShellLoader label="MoHAP" />;
  }

  if (!workspace) {
    return (
      <div data-mohap className="flex min-h-screen items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="mohap-section-label mb-2">Access denied</div>
          <p className="text-sm leading-relaxed" style={{ color: "var(--mohap-text-secondary)" }}>
            This workspace is not visible to your account. Contact your Ministry administrator.
          </p>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div data-mohap className="flex min-h-screen items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="mohap-section-label mb-2">No role assigned</div>
          <p className="text-sm leading-relaxed" style={{ color: "var(--mohap-text-secondary)" }}>
            Your account has no role in the Ministry workspace. The administrator must assign one before access.
          </p>
        </div>
      </div>
    );
  }

  // Hold rendering until WorkspaceContext has actually switched.
  if (currentWorkspace?.id !== workspace.id) {
    return <ShellLoader label="MoHAP" />;
  }

  // /mohap or /mohap/  → /mohap (chat). The role's landing_route field
  // is no longer used; every role lands in the same chat now.
  const themeStyle = {
    "--ministry-primary": workspace.branding.primary_color,
    "--ministry-accent": workspace.branding.accent_color,
  } as React.CSSProperties;

  // NChatProvider lives at the App root (May 2026) — see App.tsx. Mounting
  // it here would create a second WebSocket and double-fire chat events.
  return (
    <div data-mohap className="flex min-h-screen flex-col" style={themeStyle}>
      <Outlet />
    </div>
  );
}
